import { HistoryRange } from "./requests/request";

/** Same family as the bandwidth charts, so every KE chart reads as one set. */
export const LOSS_COLOR = "var(--color-primitive-brand-surf-700)";
export const RTT_COLOR = "var(--color-primitive-brand-surf-500)";
/** Baselines are annotations, not series: neutral and dashed. */
export const BASELINE_COLOR = "var(--color-semantic-text-secondary)";

/**
 * The baselines are two horizontal lines, one per percentile.
 *
 * Not a shaded box: a rectangle draws vertical edges at the plot borders
 * that mean nothing, and the fill dominated a chart whose series hugs the
 * lower edge. And not two near-identical dashes either — the first attempt
 * used "6 3" and "2 3" and nobody could tell them apart. A long dash
 * against a round dot reads at a glance.
 *
 * Both sit at the same low weight on purpose: p50 is by definition where
 * the series spends half its time, so a heavy line there fights the data
 * it is meant to annotate. When the two percentiles land a fraction of a
 * millisecond apart they render as one line, which is the honest picture —
 * nothing can draw two distinct things a pixel apart, and the legend
 * carries both numbers.
 */
export const TYPICAL_DASH = "10 5";
export const UNUSUAL_DASH = "1 4";
export const BASELINE_OPACITY = 0.5;

export const RANGE_OPTIONS: { value: HistoryRange; label: string }[] = [
    { value: "1h", label: "Last hour" },
    { value: "6h", label: "Last 6 hours" },
    { value: "24h", label: "Last 24 hours" },
    { value: "7d", label: "Last 7 days" },
];

export const DEFAULT_RANGE: HistoryRange = "6h";

export const rangeLabel = (range: HistoryRange): string =>
    RANGE_OPTIONS.find((opt) => opt.value === range)?.label ?? range;

export const rangeFromLabel = (label: string): HistoryRange =>
    RANGE_OPTIONS.find((opt) => opt.label === label)?.value ?? DEFAULT_RANGE;

/**
 * Ticks carry the day only when the range spans more than one: over 7 days
 * a bare HH:mm repeats seven times and the axis stops meaning anything.
 */
export const formatPointTime = (iso: string, range: HistoryRange): string => {
    const date = new Date(iso);
    const time = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    if (range !== "7d") return time;
    const day = date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
    });
    return `${day} ${time}`;
};

/** "per minute" / "per 15 min" / "per hour" caption for the aggregation window. */
export const windowLabel = (everySeconds: number): string => {
    const minutes = Math.round(everySeconds / 60);
    if (minutes <= 1) return "per minute";
    if (minutes % 60 !== 0) return `per ${minutes} min`;
    const hours = minutes / 60;
    return hours === 1 ? "per hour" : `per ${hours} h`;
};

/** Axis/grid/tooltip styling shared by both charts of the modal. */
export const GRID_STROKE = "var(--color-semantic-border-primary)";

export const AXIS_TICK = {
    fontSize: 11,
    fill: "var(--color-semantic-text-secondary)",
};

export const AXIS_LABEL_STYLE = {
    fontSize: 10,
    fill: "var(--color-semantic-text-secondary)",
};

export const TOOLTIP_CONTENT_STYLE = {
    fontSize: 12,
    borderRadius: 6,
    border: "1px solid var(--color-semantic-border-primary)",
};

export const TOOLTIP_LABEL_STYLE = {
    color: "var(--color-semantic-text-primary)",
};

/** 10% of head- and legroom so the RTT line never touches the frame. */
const PADDING_RATIO = 0.1;

/**
 * Y domain of the RTT chart.
 *
 * RTT never starts at zero: a 40-45 ms link would render as a flat line
 * against a 0-45 axis and every degradation would be invisible. The
 * baseline is folded in so its reference line is always on screen.
 */
export const rttDomain = (
    values: (number | null)[],
    references: (number | null)[] = []
): [number, number] => {
    const known = [...values, ...references].filter(
        (value): value is number => value !== null
    );
    if (known.length === 0) return [0, 1];

    const min = Math.min(...known);
    const max = Math.max(...known);
    const padding = Math.max((max - min) * PADDING_RATIO, 1);
    return [Math.max(min - padding, 0), max + padding];
};
